import { UIElement } from '../ui-element';
import { Component } from '../../decorators/component.decorator';
import styles from './ui-tile-button.css';
import { Attribute } from '../../decorators/attribute.decorator';
import { element } from '../../global/ui-helpers';
import { UIIcon } from '../icon/ui-icon';
import { UIBadge } from '../badge/ui-badge';
import { Child } from '../../decorators/child.decorator';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UITileButton
 * @element ui-tile-button
 * @classdesc `ui-tile-button` represents a single tile button element. It is designed to be used within a `ui-tile-buttons` container.
 * @property {string} [label=''] {@attr label} Label text.
 * @property {string} [href="#"] {@attr href} Link url. Only relevant if `is-button` attribute is not set to true.
 * @property {UIIconGlyphRedesign} [glyph] {@attr glyph} The icon to be displayed on the tile button.
 * @property {boolean} [isButton=false] {@attr is-button} Whether to use button instead of link tag.
 * @property {string} [badgeLabel=''] {@attr badge-label} A visible label for an optional badge (usually a number). Note you must provide an aria-label on the host element that contains an accessible name for the button and includes both label and badgeLabel, eg "Offers, 5 unread"
 * @property {HTMLAnchorElement | HTMLButtonElement} button Read-only. Shortcut to interactive element.
 * @example
 * <ui-tile-buttons aria-label="Navigation links">
 *  <ui-tile-button glyph="redesign-atm-in-out" label="Button 1" href="#link-1"></ui-tile-button>
 *  <ui-tile-button glyph="redesign-chat-bubbles" label="Button 2" href="#link-2"></ui-tile-button>
 *  <ui-tile-button glyph="redesign-circle-percent" is-button label="Button 3"></ui-tile-button>
 *  <ui-tile-button glyph="redesign-file-arrow-right" is-button label="An offers button" badge-label="3" aria-label="An offers button, 3 unread"></ui-tile-button>
 * </ui-tile-buttons>
 */

@Component({
    tag: 'ui-tile-button',
    styles,
})
export class UITileButton extends UIElement {
    @Attribute() label!: string;
    @Attribute() glyph!: string;
    @Attribute() href!: string;
    @Attribute(Boolean) isButton!: boolean;
    @Attribute() badgeLabel!: string;
    @Child('.ui-tile-button__button') button!:
        | HTMLAnchorElement
        | HTMLButtonElement;

    /**
     * Renders the badge.
     * @private
     * @returns {UIBadge}
     */
    renderBadge(): UIBadge {
        return element`<ui-badge aria-hidden="true" class="ui-tile-button__badge" layout="pin" content-type="count" color="warning">${this.badgeLabel}</ui-badge>`;
    }

    /**
     * Renders the icon.
     * @private
     * @returns {UIIcon}
     */
    renderIcon(): UIIcon {
        return element`<ui-icon class="ui-tile-button__icon" size="medium" glyph="${this.glyph}"></ui-icon>`;
    }

    /**
     * Renders the label.
     * @private
     * @returns {HTMLElement}
     */
    renderLabel(): HTMLElement {
        return element`<span class="ui-tile-button__label">${this.label}</span>`;
    }

    /**
     * Passes click when invoked on host to the nested button. Serves as an API sugar allowing programmatic clicks on the host element.
     * @param {MouseEvent} event
     * @private
     */
    handleHostClick = (event: MouseEvent) => {
        if (this.isButton && this.button && event.target === this) {
            this.button.dispatchEvent(
                new MouseEvent('click', { ...event, bubbles: false })
            );
        }
    };

    /**
     * @private
     */
    subscribeEvents() {
        if (this.isButton) {
            this.addEventListener('click', this.handleHostClick);
        }
    }

    /**
     * @private
     */
    unsubscribeEvents() {
        if (this.isButton) {
            this.removeEventListener('click', this.handleHostClick);
        }
    }

    /**
     * @inheritDoc
     */
    render(): void {
        this.updateElement({
            classList: { 'ui-tile-button': true },
            attributes: { role: 'listitem' },
        });

        let interactiveEl: HTMLButtonElement | HTMLAnchorElement;
        if (this.isButton) {
            interactiveEl = element`
                <button type="button" class="ui-tile-button__button">
                    ${this.glyph && this.renderIcon()}
                    ${this.label && this.renderLabel()}
                    ${this.badgeLabel && this.renderBadge()}
                </button>
            `;
        } else {
            interactiveEl = element`
                <a href="${this.href}" class="ui-tile-button__button">
                    ${this.glyph && this.renderIcon()}
                    ${this.label && this.renderLabel()}
                    ${this.badgeLabel && this.renderBadge()}
                </a>`;
        }

        // Move aria-label from host to interactive element to serve a full accessible name (aria-label can only be attached to interactive element)
        if (this.hasAttribute('aria-label')) {
            interactiveEl.setAttribute(
                'aria-label',
                this.getAttribute('aria-label')!
            );
            this.removeAttribute('aria-label');
        }

        this.append(interactiveEl);
    }

    /**
     * @inheritDoc
     */
    hydrate() {
        this.subscribeEvents();
    }

    /**
     * @inheritDoc
     */
    disconnect() {
        this.unsubscribeEvents();
    }

    /**
     * @inheritDoc
     */
    reconnect() {
        this.subscribeEvents();
    }
}
